import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, FileSpreadsheet, History, ListChecks, Printer, RotateCcw, Search, Users } from 'lucide-react';
import * as hr from '../../api';
import Loader from '../../components/Loader';
import { DateInput, Select } from '../../components/Controls';
import { download, fmtMoney, toCSV } from '../../format';
import { Chip, Kpi } from '../attendance/shared';
import { Empty, STATUS_LABEL, StatusChip, TypeChip, WAITING, dmy, num, payrollImpact } from './shared';
import { balanceStatus } from './BalanceHub';

// Leave reports (Stephanet's leavemgmt/Reports.jsx): each is a filtered, exportable table built from the live leave records

const REQ_COLS = [
  ['Request ID', r => r.reference, 'id'], ['Employee', r => r.name, 'b'], ['Dept', r => r.department], ['Type', r => r.leave_type, 'type'],
  ['Start Date', r => dmy(r.start_date)], ['End Date', r => dmy(r.end_date)], ['Working Days', r => r.days, 'c'], ['Reason', r => r.reason],
  ['Applied Date', r => dmy(r.applied_on)], ['Status', r => r.status, 'status'], ['Approved By', r => r.approved_by || '—'], ['Payroll Impact', r => payrollImpact(r)],
];
const MON_COLS = [
  ['Request ID', m => m.reference, 'id'], ['Employee', m => m.name, 'b'], ['Dept', m => m.department], ['Leave Year', m => m.leave_year, 'c'],
  ['Leave Type', m => m.leave_type, 'type'], ['Days', m => num(m.days), 'c'], ['Daily Rate', m => fmtMoney(m.daily_rate), 'r'],
  ['Gross', m => fmtMoney(m.gross_amount), 'r'], ['Tax', m => fmtMoney(m.tax_amount), 'r'], ['Net', m => fmtMoney(m.net_amount), 'r'],
  ['Payroll Month', m => m.payroll_month], ['Status', m => m.status], ['Payment', m => m.payment_status],
];
const REPORTS = [
  ['requests', 'Leave Request Report'], ['approved', 'Approved Leave Report'], ['pending', 'Pending Leave Report'], ['rejected', 'Rejected Leave Report'],
  ['balance', 'Employee Leave Balance Report'], ['usage', 'Leave Usage Report'], ['department', 'Department Leave Report'],
  ['carry', 'Leave Carry Forward Report'], ['encash', 'Leave Encashment Report'], ['monetization', 'Leave Monetization Report'],
  ['unpaid', 'Unpaid Leave Report'], ['resumption', 'Leave Resumption Report'], ['audit', 'Leave Audit Trail Report'], ['liability', 'Leave Liability Report'],
];
const STATUS_FILTER = { requests: true, usage: true, department: true };

export default function Reports({ data, year, setYear }) {
  const [rep, setRep] = useState('requests');
  const [dept, setDept] = useState('');
  const [status, setStatus] = useState('');
  const [audit, setAudit] = useState(null);
  const [activeOnly, setActiveOnly] = useState(true);
  useEffect(() => { if (rep === 'audit' && !audit && data.can.audit) hr.getLeaveEvents().then(r => setAudit(r.events)).catch(() => setAudit([])); }, [rep]); // eslint-disable-line react-hooks/exhaustive-deps
  const wd = data.working_days_per_month || 22;
  const today = data.today;

  const { cols, rows, days, people } = useMemo(() => {
    const inYear = r => r.start_date.startsWith(String(year));
    const deptOk = x => !dept || x.department === dept;
    const statusOk = r => !status || (status === 'waiting' ? WAITING.includes(r.status) : r.status === status);
    const reqs = data.requests.filter(r => inYear(r) && deptOk(r));
    const out = (c, rs, d = null) => ({ cols: c, rows: rs, days: d ?? rs.reduce((s, r) => s + (Number(r.days) || 0), 0), people: new Set(rs.map(r => r.employee_id ?? r.name)).size });
    switch (rep) {
      case 'requests': return out(REQ_COLS, reqs.filter(statusOk));
      case 'approved': return out(REQ_COLS, reqs.filter(r => r.status === 'approved'));
      case 'pending': return out(REQ_COLS, reqs.filter(r => WAITING.includes(r.status)));
      case 'rejected': return out([...REQ_COLS, ['Rejection Reason', r => r.comment || '—']], reqs.filter(r => r.status === 'rejected'));
      case 'unpaid': return out([...REQ_COLS.slice(0, 10), ['Deduction Days', r => r.days, 'c'],
        ['Est. Deduction', r => { const e = data.employees.find(x => x.id === r.employee_id); return e ? fmtMoney((e.basic_salary / wd) * r.days) : '—'; }, 'r']], reqs.filter(r => !r.paid && statusOk(r)));
      case 'resumption': {
        const rs = reqs.filter(r => r.status === 'approved').sort((a, b) => a.resumption.localeCompare(b.resumption));
        return out([['Request ID', r => r.reference, 'id'], ['Employee', r => r.name, 'b'], ['Dept', r => r.department], ['Type', r => r.leave_type, 'type'],
          ['Start Date', r => dmy(r.start_date)], ['End Date', r => dmy(r.end_date)], ['Days', r => r.days, 'c'], ['Resumption Date', r => dmy(r.resumption), 'b'],
          ['Leave Status', r => (r.start_date > today ? 'Upcoming' : r.end_date >= today ? 'On Leave' : 'Resumed'), 'chip'], ['Reliever', r => r.reliever || '—']], rs);
      }
      case 'usage': return out(REQ_COLS, reqs.filter(statusOk));
      case 'department': {
        const by = {};
        reqs.filter(statusOk).forEach(r => {
          const k = r.department || 'Unassigned';
          const x = by[k] || (by[k] = { dept: k, requests: 0, days: 0, approved: 0, pending: 0, rejected: 0, emps: new Set() });
          x.requests += 1; x.days += r.days || 0; x.emps.add(r.employee_id);
          if (r.status === 'approved') x.approved += 1; if (WAITING.includes(r.status)) x.pending += 1; if (r.status === 'rejected') x.rejected += 1;
        });
        const rs = Object.values(by).sort((a, b) => b.days - a.days);
        return out([['Department', x => x.dept, 'b'], ['Requests', x => x.requests, 'c'], ['Total Days', x => x.days, 'c'], ['Approved', x => x.approved, 'c'],
          ['Pending', x => x.pending, 'c'], ['Rejected', x => x.rejected, 'c'], ['Employees', x => x.emps.size, 'c']], rs, rs.reduce((s, x) => s + x.days, 0));
      }
      case 'balance': {
        // Remaining = Entitlement + CF + Adjusted − Used − Pending − Expired
        const rs = data.employees.filter(deptOk).flatMap(e => Object.entries(e.balances).map(([t, b]) => ({ ...b, type: t, name: e.name, employee_id: e.id, employee_no: e.employee_no, department: e.department })))
          .filter(x => !activeOnly || x.used || x.pending || x.carried || x.adjusted || x.monetized || x.expired);
        return out([['Employee', x => x.name, 'b'], ['Leave Type', x => x.type, 'type'], ['Year', () => year, 'c'], ['Entitlement', x => num(x.entitled), 'c'],
          ['Used', x => num(x.used + x.monetized), 'c'], ['Pending', x => num(x.pending), 'c'], ['CF', x => num(x.carried), 'c'], ['Adjusted', x => num(x.adjusted), 'c'],
          ['Expired', x => num(x.expired || 0), 'c'], ['Remaining', x => num(x.available), 'cb'], ['Status', x => balanceStatus(x)[0], 'bstat']], rs, rs.reduce((s, x) => s + x.used, 0));
      }
      case 'liability': {
        const rs = data.employees.filter(deptOk).filter(e => e.balances.Annual).map(e => {
          const rate = e.basic_salary / wd;
          return { ...e, days: e.balances.Annual.remaining, rate, amount: Math.max(0, e.balances.Annual.remaining) * rate };
        }).sort((a, b) => b.amount - a.amount);
        return out([['Employee ID', x => x.employee_no, 'id'], ['Employee', x => x.name, 'b'], ['Dept', x => x.department], ['Grade', x => x.grade || '—'],
          ['Annual Leave Balance', x => num(x.days), 'c'], [`Daily Rate (basic ÷ ${wd})`, x => fmtMoney(x.rate), 'r'], ['Liability', x => fmtMoney(x.amount), 'rb']], rs, rs.reduce((s, x) => s + Math.max(0, x.days), 0));
      }
      case 'carry': {
        const rs = data.adjustments.filter(a => a.kind === 'carry_forward' && (a.leave_year === year || a.from_year === year)).filter(deptOk);
        return out([['Employee', a => a.name, 'b'], ['Dept', a => a.department], ['From → To', a => `${a.from_year} → ${a.leave_year}`, 'c'],
          ['Leave Type', a => a.leave_type, 'type'], ['Unused Days', a => num(a.unused_days), 'c'], ['Max Allowed', a => num(a.max_allowed), 'c'],
          ['Days Carried', a => num(a.days), 'cb'], ['Expiry Date', a => (a.expiry_date ? dmy(a.expiry_date) : 'No expiry')],
          ['Status', a => a.cf_status || 'Approved', 'cfstat'], ['Approved By', a => a.approved_by || '—'], ['Date', a => dmy((a.created || '').slice(0, 10))]],
        rs, rs.reduce((s, a) => s + a.days, 0));
      }
      case 'encash':
      case 'monetization': {
        const rs = data.monetizations.filter(m => m.leave_year === year && deptOk(m) && (rep === 'monetization' || m.status === 'approved'));
        return out(MON_COLS, rs, rs.reduce((s, m) => s + m.days, 0));
      }
      case 'audit': {
        const rs = (audit || []).filter(e => e.date && e.date.startsWith(String(year)) && (!dept || data.employees.find(x => x.id === e.employee_id)?.department === dept));
        return out([['Audit ID', e => e.audit_id, 'id'], ['Date', e => e.date], ['Time', e => e.time], ['User', e => e.user, 'b'], ['Role', e => e.role],
          ['Employee Affected', e => e.employee || '—'], ['Request ID', e => e.reference || '—', 'id'], ['Action', e => e.action], ['Old Value', e => e.old_value || '—'],
          ['New Value', e => e.new_value || '—'], ['Comment', e => e.comment || '—']], rs.map(e => ({ ...e, name: e.employee })), 0);
      }
      default: return out([], []);
    }
  }, [rep, data, dept, status, year, audit, activeOnly, wd, today]);

  const title = REPORTS.find(r => r[0] === rep)[1];
  const generated = new Date().toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const text = (c, x) => { const v = c[1](x); return c[2] === 'status' ? STATUS_LABEL[v] || v : v; };
  const exportCsv = () => download(`${title.toLowerCase().replace(/ /g, '-')}-${year}.csv`, toCSV(cols.map(c => ({ label: c[0], get: x => text(c, x) })), rows));
  const years = [year - 3, year - 2, year - 1, year, year + 1].filter(y => y <= Number(data.today.slice(0, 4)) + 1);
  return (
    <div className="lvm-reports">
      <div className="mdr-card lvm-rep-list no-print">
        <div className="lvm-rep-head">REPORTS</div>
        {REPORTS.map(([k, l]) => <button key={k} className={rep === k ? 'active' : ''} onClick={() => setRep(k)}>{l}</button>)}
      </div>
      <div className="lvm-rep-body">
        <div className="mdr-card lvm-rep-bar no-print">
          <Select value={dept} onChange={e => setDept(e.target.value)}><option value="">All Departments</option>{data.departments.map(d => <option key={d} value={d}>{d}</option>)}</Select>
          {STATUS_FILTER[rep] && <Select value={status} onChange={e => setStatus(e.target.value)}><option value="">All Status</option><option value="waiting">Pending</option>
            {['approved', 'rejected', 'cancelled', 'draft', 'returned'].map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}</Select>}
          {rep === 'balance' && <label className="lvm-check"><input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} /> With activity only</label>}
          <Select value={year} onChange={e => setYear(Number(e.target.value))}>{years.map(y => <option key={y} value={y}>{y}</option>)}</Select>
          <span className="grow" />
          {data.can.export && <button className="mdr-btn" onClick={exportCsv} disabled={!rows.length}><FileSpreadsheet size={13} /> Excel</button>}
          <button className="mdr-btn" onClick={() => window.print()}><Printer size={13} /> Print</button>
        </div>
        <div className="md-kpis mds-kpis lvm-kpis3">
          <Kpi label="Total Records" value={rows.length} Ico={ListChecks} tone="blue" />
          <Kpi label="Total Days" value={num(days)} Ico={CalendarDays} tone="teal" i={1} />
          <Kpi label="Employees" value={people} Ico={Users} tone="purple" i={2} />
        </div>
        <div className="mdr-card mdr-table-card">
          <div className="mds-table-head"><b>{title}</b><span className="mds-note-inline">Generated: {generated}{rep !== 'audit' ? ` · ${year}` : ''}{dept ? ` · ${dept}` : ''}</span></div>
          <div className="mdr-scroll">
            <table className="mdr-table mds-table">
              <thead><tr>{cols.map(c => <th key={c[0]} className={/^c/.test(c[2] || '') ? 'c' : /^r/.test(c[2] || '') ? 'r' : ''}>{c[0]}</th>)}</tr></thead>
              <tbody key={rep}>
                {rows.map((x, i) => (
                  <tr key={x.id ?? `${rep}${i}`} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 12}ms` }}>
                    {cols.map(c => {
                      const v = c[1](x);
                      const k = c[2] || '';
                      if (k === 'status') return <td key={c[0]}><StatusChip s={v} /></td>;
                      if (k === 'type') return <td key={c[0]}><TypeChip t={v} /></td>;
                      if (k === 'cfstat') return <td key={c[0]}><Chip tone={v === 'Expired' ? 'gray' : 'green'}>{v}</Chip></td>;
                      if (k === 'bstat') return <td key={c[0]}><Chip tone={balanceStatus(x)[1]}>{v}</Chip></td>;
                      if (k === 'chip') return <td key={c[0]}><Chip tone={v === 'On Leave' ? 'teal' : v === 'Upcoming' ? 'blue' : 'green'}>{v}</Chip></td>;
                      return <td key={c[0]} className={`${k === 'id' ? 'mdr-id' : ''} ${k.includes('b') ? 'b' : ''} ${k.startsWith('c') ? 'c' : k.startsWith('r') ? 'r' : ''}`}>{v ?? '—'}</td>;
                    })}
                  </tr>
                ))}
                {!rows.length && (rep === 'audit' && data.can.audit && !audit
                  ? <tr><td colSpan={cols.length || 1}><Loader title="Loading audit trail…" subtitle="Fetching leave activity" /></td></tr>
                  : <Empty cols={cols.length || 1}>{rep === 'audit' && !data.can.audit ? 'You need approve rights to see the audit trail.' : 'No records for these filters.'}</Empty>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// Advanced Dashboard > Audit Trail: every step of every leave record — who did it, their role, the employee affected, the record,
// and the value before and after (requests, approvals, attendance and balance updates, conflicts, monetization, adjustments)
const EVENT_TONE = {
  'Leave request created': 'gray', 'Leave request submitted': 'blue', 'Leave pre-approved': 'indigo', 'Leave approved': 'green', 'Leave rejected': 'red',
  'Leave returned for correction': 'orange', 'Leave cancelled': 'gray', 'Attendance updated': 'teal', 'Leave balance updated': 'purple',
  'Leave conflict detected': 'darkred', 'Monetization requested': 'yellow', 'Leave monetized': 'green', 'Monetization rejected': 'red',
  'Monetization returned': 'orange', 'Leave balance adjusted': 'orange', 'Leave carried forward': 'teal',
};
const AUDIT_EMPTY = { action: '', employee_id: '', start: '', end: '', q: '' };

export function AuditTrail({ data }) {
  const [f, setF] = useState(AUDIT_EMPTY);
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => { const t = setTimeout(() => setF(x => (x.q === q ? x : { ...x, q })), 350); return () => clearTimeout(t); }, [q]);
  useEffect(() => {
    if (!data.can.audit) return;
    hr.getLeaveEvents(Object.fromEntries(Object.entries(f).filter(([, v]) => v))).then(r => { setRes(r); setError(''); }).catch(e => setError(e.message));
  }, [f, data.can.audit]);
  if (!data.can.audit) return <div className="mdr-card mdr-empty">You need approve rights to see the leave audit trail.</div>;
  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const rows = res?.events || [];
  const filtered = Object.values(f).some(Boolean);
  const exportCsv = () => download(`leave-audit-trail-${data.today}.csv`, toCSV([
    { label: 'Audit ID', get: e => e.audit_id }, { label: 'Date', get: e => e.date }, { label: 'Time', get: e => e.time }, { label: 'User', get: e => e.user },
    { label: 'Role', get: e => e.role }, { label: 'Employee Affected', get: e => e.employee }, { label: 'Request ID', get: e => e.reference },
    { label: 'Action', get: e => e.action }, { label: 'Old Value', get: e => e.old_value }, { label: 'New Value', get: e => e.new_value }, { label: 'Comment', get: e => e.comment },
  ], rows));
  return (
    <div className="mdr-card mdr-table-card lvm-audit">
      <div className="lvm-audit-bar no-print">
        <Select value={f.action} onChange={e => set('action', e.target.value)}>
          <option value="">All Actions</option>
          {(res?.actions || []).map(a => <option key={a} value={a}>{`${a}${res?.counts?.[a] ? ` (${res.counts[a]})` : ''}`}</option>)}
        </Select>
        <Select value={f.employee_id} onChange={e => set('employee_id', e.target.value)}>
          <option value="">All Employees</option>{data.employees.map(e => <option key={e.id} value={e.id}>{`${e.employee_no} — ${e.name}`}</option>)}
        </Select>
        <DateInput value={f.start} onChange={e => set('start', e.target.value)} />
        <DateInput value={f.end} onChange={e => set('end', e.target.value)} />
        <div className="mdr-search mds-quick"><Search size={14} /><input placeholder="Request ID, user, comment…" value={q} onChange={e => setQ(e.target.value)} /></div>
        {filtered && <button className="mdr-btn" onClick={() => { setF(AUDIT_EMPTY); setQ(''); }}><RotateCcw size={13} /> Clear</button>}
        <span className="grow" />
        {data.can.export && <button className="mdr-btn" onClick={exportCsv} disabled={!rows.length}><FileSpreadsheet size={13} /> Export</button>}
      </div>
      <div className="lvm-audit-count"><History size={14} /> <b>{res ? rows.length : '…'}</b> records{error && <span className="mds-error"> · {error}</span>}</div>
      <div className="mdr-scroll">
        <table className="mdr-table mds-table">
          <thead><tr><th>Audit ID</th><th>Date</th><th>Time</th><th>User</th><th>Role</th><th>Employee Affected</th><th>Request ID</th><th>Action</th>
            <th>Old Value</th><th>New Value</th><th>Comment</th></tr></thead>
          <tbody>
            {rows.map((e, i) => (
              <tr key={e.id} className="mdr-row" style={{ animationDelay: `${Math.min(i, 20) * 12}ms` }}>
                <td className="mdr-id">{e.audit_id}</td><td className="nowrap">{e.date}</td><td className="nowrap mono">{e.time}</td>
                <td className="nowrap b">{e.user}</td><td><Chip tone={e.role === 'System' ? 'gray' : 'blue'}>{e.role}</Chip></td>
                <td className="nowrap">{e.employee || '—'}</td><td className="mdr-id">{e.reference || '—'}</td>
                <td><Chip tone={EVENT_TONE[e.action] || 'gray'}>{e.action}</Chip></td>
                <td className="nowrap lvm-old">{e.old_value || '—'}</td><td className="nowrap lvm-new">{e.new_value || '—'}</td>
                <td className="lvm-audit-comment">{e.comment || '—'}</td>
              </tr>
            ))}
            {res && !rows.length && <Empty cols={11}>{filtered ? 'No audit records match these filters.' : 'No leave activity recorded yet.'}</Empty>}
            {!res && !error && <tr><td colSpan={11}><Loader title="Loading audit trail…" subtitle="Fetching leave activity" /></td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
